import { createSignal, onMount, onCleanup, For, Show, createMemo, Switch, Match } from "solid-js";
import { api, type TableInfo, type QueryResult, type ColumnInfo, type IndexInfo, type FKInfo, type MigrationStatus, type MigrationDetail } from "../shared";
import { Pagination } from "./Pagination";

export function DatabasePanel() {
  const [tables, setTables] = createSignal<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = createSignal("");
  const [tableData, setTableData] = createSignal<QueryResult | null>(null);
  const [tableSchema, setTableSchema] = createSignal<ColumnInfo[]>([]);
  const [tableIndexes, setTableIndexes] = createSignal<IndexInfo[]>([]);
  const [tableForeignKeys, setTableForeignKeys] = createSignal<FKInfo[]>([]);
  const [tablePage, setTablePage] = createSignal(1);
  const [sqlInput, setSqlInput] = createSignal("");
  const [sqlResult, setSqlResult] = createSignal<QueryResult | null>(null);
  const [sqlPage, setSqlPage] = createSignal(1);
  const [error, setError] = createSignal("");
  const [loading, setLoading] = createSignal<string | null>(null);
  const [viewMode, setViewMode] = createSignal<"browse" | "query" | "schema">("browse");
  const [migrating, setMigrating] = createSignal(false);
  const [migrateResult, setMigrateResult] = createSignal("");
  const [seeding, setSeeding] = createSignal(false);
  const [editingRow, setEditingRow] = createSignal<number | null>(null);
  const [rowEdits, setRowEdits] = createSignal<Record<number, Record<number, string>>>({});
  const [addingRow, setAddingRow] = createSignal(false);
  const [newRowValues, setNewRowValues] = createSignal<Record<number, string>>({});
  const [deletingRow, setDeletingRow] = createSignal<number | null>(null);
  const [migrationList, setMigrationList] = createSignal<MigrationStatus[]>([]);
  const [subTab, setSubTab] = createSignal<"explorer" | "migrations">("explorer");
  const [tablePerPage, setTablePerPage] = createSignal(10);
  const [sqlPerPage, setSqlPerPage] = createSignal(10);
  const [migrationLoading, setMigrationLoading] = createSignal<string | null>(null);
  const [migrationDetail, setMigrationDetail] = createSignal<MigrationDetail | null>(null);
  const [migrationDetailLoading, setMigrationDetailLoading] = createSignal<string | null>(null);

  const pendingCount = createMemo(() => migrationList().filter((m) => !m.applied).length);
  const nextPending = createMemo(() => migrationList().find((m) => !m.applied)?.name ?? null);

  const isMutation = (sql: string) =>
    ["INSERT", "UPDATE", "DELETE", "CREATE", "DROP", "ALTER"].some((kw) =>
      sql.trim().toUpperCase().startsWith(kw),
    );

  const tablePageCount = createMemo(() => {
    const d = tableData();
    return d ? Math.max(1, Math.ceil(d.rowCount / tablePerPage())) : 1;
  });
  const tablePageRows = createMemo(() => {
    const d = tableData();
    if (!d) return [];
    const start = (tablePage() - 1) * tablePerPage();
    return d.rows.slice(start, start + tablePerPage());
  });
  const sqlPageCount = createMemo(() => {
    const d = sqlResult();
    return d ? Math.max(1, Math.ceil(d.rowCount / sqlPerPage())) : 1;
  });
  const sqlPageRows = createMemo(() => {
    const d = sqlResult();
    if (!d) return [];
    const start = (sqlPage() - 1) * sqlPerPage();
    return d.rows.slice(start, start + sqlPerPage());
  });

  async function loadTables() {
    try { setTables(await api.db.tables()); } catch (e: any) { setError(e.message); }
  }

  async function loadMigrations() {
    try { setMigrationList(await api.db.migrations()); } catch {}
  }

  function hashTable(): string | null {
    const h = location.hash.replace(/^#/, "");
    if (h.startsWith("database/")) {
      const rest = decodeURIComponent(h.slice("database/".length));
      if (rest === "migrations") return null;
      return rest;
    }
    return null;
  }

  function hashSubTab(): "explorer" | "migrations" {
    const h = location.hash.replace(/^#/, "");
    return h === "database/migrations" ? "migrations" : "explorer";
  }

  async function loadTableData(table: string) {
    setSelectedTable(table);
    location.hash = `database/${encodeURIComponent(table)}`;
    setLoading("table");
    setViewMode("browse");
    setTablePage(1);
    setEditingRow(null);
    setRowEdits({});
    setAddingRow(false);
    try {
      const [data, schema, indexes, fks] = await Promise.all([
        api.db.query(`SELECT * FROM "${table}" LIMIT 500`),
        api.db.tableSchema(table),
        api.db.tableIndexes(table).catch(() => [] as IndexInfo[]),
        api.db.tableForeignKeys(table).catch(() => [] as FKInfo[]),
      ]);
      const countResult = await api.db.query(`SELECT COUNT(*) as cnt FROM "${table}"`);
      data.rowCount = (countResult.rows[0]?.[0] as number) ?? data.rowCount;
      setTableData(data);
      setTableSchema(schema);
      setTableIndexes(indexes);
      setTableForeignKeys(fks);
      setError("");
    } catch (e: any) { setError(e.message); }
    setLoading(null);
  }

  async function executeQuery() {
    const sql = sqlInput().trim();
    if (!sql) return;
    setLoading("sql");
    setSqlPage(1);
    try {
      const result = isMutation(sql) ? await api.db.mutate(sql) : await api.db.query(sql);
      if (isMutation(sql)) {
        setSqlResult({ columns: ["result"], rows: [[`${result.changes} row(s) affected`]], rowCount: 1 });
        setMigrateResult(`Query OK, ${result.changes} row(s) affected`);
        const selTable = selectedTable();
        if (selTable) { loadTableData(selTable); await loadTables(); }
      } else {
        setSqlResult(result);
      }
      setError("");
    } catch (e: any) { setError(e.message); }
    setLoading(null);
  }

  async function runMigrate() {
    setMigrating(true); setMigrateResult("");
    try {
      const result = await api.db.migrate();
      setMigrateResult(`Applied: ${result.applied.join(", ") || "none"}`);
      await loadTables();
      await loadMigrations();
    } catch (e: any) { setMigrateResult(`Error: ${e.message}`); }
    setMigrating(false);
  }

  async function runSeed() {
    setSeeding(true);
    try {
      const result = await api.db.seed();
      setError("");
      setMigrateResult(result.message);
      const selTable = selectedTable();
      if (selTable) loadTableData(selTable);
      await loadTables();
    } catch (e: any) { setError(e.message); }
    setSeeding(false);
  }

  async function applyMigration(name: string) {
    setMigrationLoading(name);
    try {
      await api.db.migrateOne(name);
      setMigrateResult(`Applied: ${name}`);
      await loadMigrations();
    } catch (e: any) { setError(e.message); }
    setMigrationLoading(null);
  }

  async function viewMigration(name: string) {
    setMigrationDetailLoading(name);
    try {
      setMigrationDetail(await api.db.migrationDetail(name));
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
    setMigrationDetailLoading(null);
  }

  async function applyNextMigration() {
    const name = nextPending();
    if (name) await applyMigration(name);
  }

  async function applyAllMigrations() {
    setMigrationLoading("all");
    try {
      const result = await api.db.migrate();
      setMigrateResult(`Applied: ${result.applied.join(", ") || "none"}`);
      await loadMigrations();
    } catch (e: any) { setMigrateResult(`Error: ${e.message}`); }
    setMigrationLoading(null);
  }

  function startEditingRow(rowIdx: number) {
    setEditingRow(rowIdx);
    // Initialize edits from current row values
    const row = tablePageRows()[rowIdx];
    const edits: Record<number, string> = {};
    row.forEach((val, colIdx) => { edits[colIdx] = val === null ? "" : String(val); });
    setRowEdits((p) => ({ ...p, [rowIdx]: edits }));
  }

  function cancelRowEdit() {
    const r = editingRow();
    if (r != null) {
      setRowEdits((p) => { const next = { ...p }; delete next[r]; return next; });
    }
    setEditingRow(null);
  }

  async function saveRow(rowIdx: number) {
    const table = selectedTable();
    const cols = tableData()?.columns ?? [];
    const original = tablePageRows()[rowIdx];
    const edits = rowEdits()[rowIdx];
    if (!edits) return;

    const idCol = tableSchema().find((c) => c.name.toLowerCase() === "id")?.name;
    const idIndex = cols.indexOf(idCol ?? "id");
    const idVal = idIndex >= 0 ? original[idIndex] : null;

    if (!idCol || idVal == null) {
      setError("Cannot update: no ID column found or ID is null");
      return;
    }

    const setClauses: string[] = [];
    for (let j = 0; j < cols.length; j++) {
      const orig = original[j] === null ? "" : String(original[j]);
      const edited = edits[j] ?? orig;
      if (edited === orig) continue;
      const val = edited === "" ? "NULL" : `'${edited.replace(/'/g, "''")}'`;
      setClauses.push(`"${cols[j]}" = ${val}`);
    }

    if (setClauses.length === 0) {
      cancelRowEdit();
      return;
    }

    const sql = `UPDATE "${table}" SET ${setClauses.join(", ")} WHERE "${idCol}" = '${String(idVal).replace(/'/g, "''")}'`;

    try {
      await api.db.mutate(sql);
      cancelRowEdit();
      loadTableData(table);
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function deleteRow(rowIdx: number) {
    const table = selectedTable();
    const cols = tableData()?.columns ?? [];
    const row = tablePageRows()[rowIdx];
    const idCol = tableSchema().find((c) => c.name.toLowerCase() === "id")?.name;
    const idIndex = cols.indexOf(idCol ?? "id");
    const idVal = idIndex >= 0 ? row[idIndex] : null;
    if (!idCol || idVal == null) {
      setError("Cannot delete: no ID column found or ID is null");
      return;
    }
    try {
      await api.db.mutate(`DELETE FROM "${table}" WHERE "${idCol}" = '${String(idVal).replace(/'/g, "''")}'`);
      setDeletingRow(null);
      loadTableData(table);
      await loadTables();
    } catch (e: any) { setError(e.message); }
  }

  async function addRow() {
    const table = selectedTable();
    const cols = tableData()?.columns ?? [];
    const values: Record<number, string> = newRowValues();
    const colList: string[] = [];
    const valList: string[] = [];
    for (let i = 0; i < cols.length; i++) {
      const v = (values[i] ?? "").trim();
      if (!v) continue;
      colList.push(`"${cols[i]}"`);
      valList.push(`'${v.replace(/'/g, "''")}'`);
    }
    if (colList.length === 0) {
      setError("Enter at least one value, or use SQL Query for INSERT");
      return;
    }
    try {
      await api.db.mutate(`INSERT INTO "${table}" (${colList.join(", ")}) VALUES (${valList.join(", ")})`);
      setAddingRow(false);
      setNewRowValues({});
      loadTableData(table);
      await loadTables();
    } catch (e: any) { setError(e.message); }
  }

  function handleCellEdit(rowIdx: number, colIdx: number, value: string) {
    setRowEdits((p) => {
      const row = { ...(p[rowIdx] ?? {}) };
      row[colIdx] = value;
      return { ...p, [rowIdx]: row };
    });
  }

  onMount(() => {
    loadTables();
    loadMigrations();

    const st = hashSubTab();
    if (st === "migrations") setSubTab("migrations");
    else {
      loadTables().then(() => {
        const ht = hashTable();
        if (ht) loadTableData(ht);
      });
    }

    const onHash = () => {
      const st = hashSubTab();
      if (st === "migrations") {
        setSubTab("migrations");
      } else {
        setSubTab("explorer");
        const ht = hashTable();
        if (ht) {
          if (ht !== selectedTable()) loadTableData(ht);
        } else if (selectedTable()) {
          setSelectedTable("");
          setTableData(null);
          setTableSchema([]);
          setEditingRow(null);
          setRowEdits({});
          setAddingRow(false);
        }
      }
    };
    window.addEventListener("hashchange", onHash);
    onCleanup(() => window.removeEventListener("hashchange", onHash));
  });

  return (
    <div class="flex gap-6 h-[calc(100vh-120px)]">
      {/* Left sidebar */}
      <div class="w-64 shrink-0 border border-gray-800 rounded-lg overflow-hidden flex flex-col">
        <div class="bg-gray-900 px-4 py-3 border-b border-gray-800">
          <h3 class="text-sm font-semibold text-gray-300">Tables</h3>
        </div>
        <div class="flex-1 overflow-y-auto">
          <For each={tables()}>
            {(t) => (
              <button
                onClick={() => loadTableData(t.name)}
                class={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors cursor-pointer
                  ${selectedTable() === t.name ? "bg-cyan-900/30 text-cyan-300" : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-200"}`}
              >
                <span class="truncate">{t.name}</span>
                <span class="text-xs text-gray-600 ml-2">{t.rowCount}</span>
              </button>
            )}
          </For>
        </div>
        <div class="border-t border-gray-800 p-3 space-y-2">
          <button onClick={runSeed} disabled={seeding()} class="w-full px-3 py-2 bg-amber-900/30 border border-amber-700 text-amber-300 rounded text-xs font-medium hover:bg-amber-900/50 disabled:opacity-50 cursor-pointer">
            {seeding() ? "Seeding..." : "Seed Admin"}
          </button>
          <button
            onClick={() => { setSubTab("migrations"); location.hash = "database/migrations"; }}
            class="w-full px-3 py-2 bg-gray-800 text-gray-400 rounded text-xs font-medium hover:text-gray-200 transition-colors cursor-pointer flex items-center justify-between"
          >
            <span>Migrations</span>
            <Show when={pendingCount() > 0}>
              <span class="bg-amber-900/50 text-amber-300 text-[10px] px-1.5 py-0.5 rounded">{pendingCount()} pending</span>
            </Show>
          </button>
        </div>
      </div>

      {/* Right content */}
      <div class="flex-1 flex flex-col min-w-0 space-y-4">
        <Show when={error()}>
          <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
            <span>{error()}</span>
            <button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 cursor-pointer">✕</button>
          </div>
        </Show>
        <Show when={migrateResult()}>
          <div class="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg text-sm">{migrateResult()}</div>
        </Show>

        <div class="flex gap-1 bg-gray-900 rounded-lg p-1 w-fit">
          <button onClick={() => { setSubTab("explorer"); location.hash = "database"; }} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer ${subTab() === "explorer" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>Explorer</button>
          <button onClick={() => { setSubTab("migrations"); location.hash = "database/migrations"; }} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer ${subTab() === "migrations" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>
            Migrations
            <Show when={pendingCount() > 0}>
              <span class="ml-1.5 bg-amber-900/50 text-amber-300 text-[10px] px-1.5 py-0.5 rounded">{pendingCount()}</span>
            </Show>
          </button>
        </div>

        <Switch>
          <Match when={subTab() === "explorer"}>
            <div class="flex gap-1 bg-gray-900 rounded-lg p-1 w-fit">
            <button onClick={() => setViewMode("browse")} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer ${viewMode() === "browse" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>Table View</button>
          <button onClick={() => setViewMode("schema")} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer ${viewMode() === "schema" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>Schema</button>
          <button onClick={() => setViewMode("query")} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer ${viewMode() === "query" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>SQL Query</button>
        </div>

        <Switch>
          <Match when={viewMode() === "browse"}>
            <Show when={selectedTable()} fallback={
              <div class="flex-1 flex items-center justify-center text-gray-600 text-sm">Select a table to browse</div>
            }>
              <div class="flex-1 flex flex-col min-h-0 border border-gray-800 rounded-lg overflow-hidden">
                <div class="bg-gray-900 px-4 py-2 border-b border-gray-800 text-sm text-gray-300 flex items-center justify-between">
                  <span>{selectedTable()} ({tableData()?.rowCount ?? 0} rows)</span>
                  <button
                    onClick={() => { setAddingRow(true); setNewRowValues({}); }}
                    disabled={addingRow()}
                    class="px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded text-xs hover:bg-cyan-900/50 disabled:opacity-50 cursor-pointer"
                  >
                    + Add Row
                  </button>
                </div>
                <div class="flex-1 overflow-auto">
                  <Show when={!loading("table")} fallback={<div class="p-4 text-gray-500 text-sm">Loading...</div>}>
                    <Show when={tableData()}>
                      <DataTable
                        columns={tableData()!.columns}
                        rows={tablePageRows()}
                        schema={tableSchema()}
                        editingRow={editingRow()}
                        rowEdits={rowEdits()}
                        onRowClick={(r) => startEditingRow(r)}
                        onDelete={deleteRow}
                        deletingRow={deletingRow()}
                        setDeletingRow={setDeletingRow}
                        table={selectedTable()}
                      />
                    </Show>
                  </Show>
                </div>

                {/* Edit row form */}
                <Show when={editingRow() != null && tablePageRows()[editingRow()!]}>
                  <EditRowForm
                    columns={tableData()!.columns}
                    row={tablePageRows()[editingRow()!]}
                    edits={rowEdits()[editingRow()!] ?? {}}
                    schema={tableSchema()}
                    table={selectedTable()}
                    onCellEdit={(colIdx, val) => handleCellEdit(editingRow()!, colIdx, val)}
                    onSave={() => saveRow(editingRow()!)}
                    onCancel={cancelRowEdit}
                  />
                </Show>

                {/* Add row form */}
                <Show when={addingRow()}>
                  <div class="border-t border-gray-700 bg-gray-950 p-4">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-xs text-gray-400">New row — enter values (empty = DB default)</span>
                      <div class="flex gap-2">
                        <button onClick={addRow} class="px-3 py-1 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded text-xs hover:bg-emerald-900/50 cursor-pointer">Insert Row</button>
                        <button onClick={() => setAddingRow(false)} class="px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:text-gray-200 cursor-pointer">Cancel</button>
                      </div>
                    </div>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      <For each={tableData()?.columns ?? []}>
                        {(col, i) => (
                          <div class="flex flex-col gap-1">
                            <label class="text-xs text-gray-500 truncate" title={col}>{col}</label>
                            <input
                              value={newRowValues()[i()] ?? ""}
                              onInput={(e) => setNewRowValues((p) => ({ ...p, [i()]: e.currentTarget.value }))}
                              class="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-cyan-600"
                              placeholder={col}
                            />
                          </div>
                        )}
                      </For>
                    </div>
                    <div class="bg-gray-900 border border-gray-700 rounded px-3 py-2">
                      <div class="text-xs text-gray-500 mb-1">SQL Preview:</div>
                      <div class="text-xs font-mono text-gray-300 break-all">{(() => {
                        const cols = tableData()?.columns ?? [];
                        const vals = newRowValues();
                        const colList: string[] = [];
                        const valList: string[] = [];
                        for (let i = 0; i < cols.length; i++) {
                          const v = (vals[i] ?? "").trim();
                          if (!v) continue;
                          colList.push(`"${cols[i]}"`);
                          valList.push(`'${v.replace(/'/g, "''")}'`);
                        }
                        if (colList.length === 0) return "(enter at least one value)";
                        return `INSERT INTO "${selectedTable()}" (${colList.join(", ")}) VALUES (${valList.join(", ")});`;
                      })()}</div>
                    </div>
                  </div>
                </Show>

                <Show when={tablePageCount() > 1 || tableData()}>
                  <Pagination
                    page={tablePage()}
                    totalPages={tablePageCount()}
                    perPage={tablePerPage()}
                    total={tableData()?.rowCount ?? 0}
                    loading={!!loading("table")}
                    onPageChange={setTablePage}
                    onPerPageChange={(next) => { setTablePerPage(next); setTablePage(1); }}
                  />
                </Show>
              </div>
            </Show>
          </Match>

          <Match when={viewMode() === "schema"}>
            <Show when={selectedTable()} fallback={
              <div class="flex-1 flex items-center justify-center text-gray-600 text-sm">Select a table to view schema</div>
            }>
              <div class="flex-1 flex flex-col min-h-0 border border-gray-800 rounded-lg overflow-hidden">
                <div class="bg-gray-900 px-4 py-2 border-b border-gray-800 text-sm text-gray-300">
                  Schema: {selectedTable()}
                </div>
                <div class="flex-1 overflow-auto">
                  <table class="w-full text-xs font-mono">
                    <thead class="bg-gray-900 text-gray-400 sticky top-0">
                      <tr>
                        <th class="text-left px-3 py-2 font-medium border-b border-gray-800">cid</th>
                        <th class="text-left px-3 py-2 font-medium border-b border-gray-800">name</th>
                        <th class="text-left px-3 py-2 font-medium border-b border-gray-800">type</th>
                        <th class="text-center px-3 py-2 font-medium border-b border-gray-800">notnull</th>
                        <th class="text-left px-3 py-2 font-medium border-b border-gray-800">dflt_value</th>
                        <th class="text-center px-3 py-2 font-medium border-b border-gray-800">pk</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-800/50">
                      <For each={tableSchema()}>
                        {(col) => (
                          <tr class={`hover:bg-gray-900/30 ${col.pk ? "bg-cyan-950/20" : ""}`}>
                            <td class="px-3 py-1.5 text-gray-500">{col.cid}</td>
                            <td class={`px-3 py-1.5 ${col.pk ? "text-cyan-400 font-semibold" : "text-gray-200"}`}>{col.name}</td>
                            <td class="px-3 py-1.5 text-gray-400">{col.type}</td>
                            <td class="px-3 py-1.5 text-center">{col.notnull ? <span class="text-emerald-400">1</span> : <span class="text-gray-600">0</span>}</td>
                            <td class="px-3 py-1.5 text-gray-500">{col.dflt_value ?? <span class="text-gray-700 italic">NULL</span>}</td>
                            <td class="px-3 py-1.5 text-center">{col.pk ? <span class="text-cyan-400">1</span> : <span class="text-gray-600">0</span>}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>

                  <Show when={tableIndexes().length > 0}>
                    <div class="bg-gray-900 px-4 py-2 border-t border-gray-800 text-sm text-gray-300 mt-2">Indexes</div>
                    <table class="w-full text-xs font-mono">
                      <thead class="bg-gray-900 text-gray-400 sticky top-0">
                        <tr>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">name</th>
                          <th class="text-center px-3 py-1.5 font-medium border-b border-gray-800">unique</th>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">origin</th>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">columns</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-800/50">
                        <For each={tableIndexes()}>
                          {(idx) => {
                            const originLabel = () => idx.origin === "pk" ? "PRIMARY KEY" : idx.origin === "u" ? "UNIQUE" : "CREATE INDEX";
                            return (
                              <tr class="hover:bg-gray-900/30">
                                <td class="px-3 py-1.5 text-gray-200 font-semibold">{idx.name}</td>
                                <td class="px-3 py-1.5 text-center">{idx.unique ? <span class="text-emerald-400">1</span> : <span class="text-gray-600">0</span>}</td>
                                <td class="px-3 py-1.5">
                                  <span class={`${idx.origin === "pk" ? "text-cyan-400" : idx.origin === "u" ? "text-amber-400" : "text-gray-400"}`}>{originLabel()}</span>
                                </td>
                                <td class="px-3 py-1.5 text-gray-300">{idx.columns.join(", ")}</td>
                              </tr>
                            );
                          }}
                        </For>
                      </tbody>
                    </table>
                  </Show>

                  <Show when={tableForeignKeys().length > 0}>
                    <div class="bg-gray-900 px-4 py-2 border-t border-gray-800 text-sm text-gray-300 mt-2">Foreign Keys</div>
                    <table class="w-full text-xs font-mono">
                      <thead class="bg-gray-900 text-gray-400 sticky top-0">
                        <tr>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">from</th>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">references</th>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">on delete</th>
                          <th class="text-left px-3 py-1.5 font-medium border-b border-gray-800">on update</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-800/50">
                        <For each={tableForeignKeys()}>
                          {(fk) => (
                            <tr class="hover:bg-gray-900/30">
                              <td class="px-3 py-1.5 text-gray-200 font-semibold">{fk.from}</td>
                              <td class="px-3 py-1.5 text-gray-300">{fk.toTable}.{fk.toColumn}</td>
                              <td class="px-3 py-1.5">
                                <span class={fk.onDelete === "CASCADE" ? "text-rose-400" : fk.onDelete === "SET NULL" ? "text-amber-400" : "text-gray-400"}>{fk.onDelete || "NO ACTION"}</span>
                              </td>
                              <td class="px-3 py-1.5 text-gray-400">{fk.onUpdate || "NO ACTION"}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </Show>
                </div>
              </div>
            </Show>
          </Match>

          <Match when={viewMode() === "query"}>
            <div class="flex-1 flex flex-col min-h-0 space-y-4">
              <div class="flex gap-3">
                <textarea
                  value={sqlInput()}
                  onInput={(e) => setSqlInput(e.currentTarget.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) executeQuery(); }}
                  placeholder="SELECT * FROM users;\nUPDATE users SET role='admin' WHERE email='...';\nINSERT INTO users (id, email, ...) VALUES (...);"
                  rows={3}
                  class="flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 font-mono focus:outline-none focus:border-cyan-600 resize-none"
                />
                <button onClick={executeQuery} disabled={!!loading("sql") || !sqlInput().trim()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 self-end cursor-pointer">
                  {loading("sql") ? "..." : "Run"}
                </button>
              </div>
              <div class="text-xs text-gray-600">Supports SELECT, INSERT, UPDATE, DELETE, CREATE, DROP, ALTER. Ctrl+Enter to run.</div>
              <Show when={sqlResult()}>
                <div class="flex-1 flex flex-col min-h-0 border border-gray-800 rounded-lg overflow-hidden">
                  <div class="bg-gray-900 px-4 py-2 border-b border-gray-800 text-sm text-gray-300">
                    {sqlResult()!.changes != null ? `${sqlResult()!.changes} row(s) affected` : `${sqlResult()!.rowCount} rows`}
                  </div>
                  <div class="flex-1 overflow-auto">
                    <DataTableStatic columns={sqlResult()!.columns} rows={sqlPageRows()} />
                  </div>
                  <Show when={sqlPageCount() > 1}>
                    <Pagination
                      page={sqlPage()}
                      totalPages={sqlPageCount()}
                      perPage={sqlPerPage()}
                      total={sqlResult()?.rowCount ?? 0}
                      loading={!!loading("sql")}
                      onPageChange={setSqlPage}
                      onPerPageChange={(next) => { setSqlPerPage(next); setSqlPage(1); }}
                    />
                  </Show>
                </div>
              </Show>
            </div>
          </Match>
        </Switch>
          </Match>
          <Match when={subTab() === "migrations"}> 
            <div class="flex-1 flex flex-col min-h-0 border border-gray-800 rounded-lg overflow-hidden">
              <div class="bg-gray-900 px-4 py-2 border-b border-gray-800 text-sm text-gray-300 flex items-center justify-between">
                <span>Migrations ({migrationList().length} files, {pendingCount()} pending)</span>
                <div class="flex gap-2">
                  <button onClick={applyNextMigration} disabled={!nextPending() || migrationLoading() !== null} class="px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded text-xs hover:bg-cyan-900/50 disabled:opacity-50 cursor-pointer">
                    {migrationLoading() !== null && migrationLoading() === nextPending() ? "Applying..." : `Run: ${nextPending()?.slice(0, 30) ?? "none"}`}
                  </button>
                  <button onClick={applyAllMigrations} disabled={pendingCount() === 0 || migrationLoading() !== null} class="px-3 py-1 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded text-xs hover:bg-emerald-900/50 disabled:opacity-50 cursor-pointer">
                    {migrationLoading() === "all" ? "Applying..." : "Run All"}
                  </button>
                </div>
              </div>
              <div class="flex-1 min-h-0 overflow-auto">
                <table class="w-full text-xs font-mono">
                  <thead class="bg-gray-900 text-gray-400 sticky top-0">
                    <tr>
                      <th class="text-left px-3 py-2 font-medium border-b border-gray-800 w-8">#</th>
                      <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Migration File</th>
                      <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Status</th>
                      <th class="text-right px-3 py-2 font-medium border-b border-gray-800 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-800/50">
                    <For each={migrationList()}>
                      {(m, i) => (
                        <tr class="hover:bg-gray-900/30">
                          <td class="px-3 py-1.5 text-gray-600">{i() + 1}</td>
                          <td class="px-3 py-1.5 text-gray-200">{m.name}</td>
                          <td class="px-3 py-1.5">
                            {m.applied
                              ? <span class="text-emerald-400 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-emerald-400" />Applied</span>
                              : <span class="text-gray-500 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-gray-600" />Pending</span>
                            }
                          </td>
                          <td class="px-3 py-1.5 text-right">
                            <div class="flex justify-end gap-2">
                              <button onClick={() => viewMigration(m.name)} disabled={migrationDetailLoading() !== null} class="text-xs px-2 py-0.5 bg-gray-800 border border-gray-700 text-gray-300 rounded hover:bg-gray-700 disabled:opacity-50 cursor-pointer">
                                {migrationDetailLoading() === m.name ? "..." : "View SQL"}
                              </button>
                              <Show when={!m.applied}>
                                <button onClick={() => applyMigration(m.name)} disabled={migrationLoading() !== null} class="text-xs px-2 py-0.5 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 disabled:opacity-50 cursor-pointer">
                                  {migrationLoading() === m.name ? "..." : "Apply"}
                                </button>
                              </Show>
                            </div>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
              <Show when={migrationDetail()}>
                <div class="shrink-0 border-t border-gray-800 bg-gray-950/60">
                  <div class="flex items-center justify-between px-4 py-2 border-b border-gray-800">
                    <div class="text-sm text-gray-300">
                      {migrationDetail()!.name}
                      <span class="ml-2 text-xs text-gray-500">({migrationDetail()!.sizeBytes} bytes)</span>
                    </div>
                    <button onClick={() => setMigrationDetail(null)} class="text-xs text-gray-500 hover:text-gray-300 cursor-pointer">Close</button>
                  </div>
                  <pre class="h-80 overflow-auto px-4 py-3 text-xs text-gray-300 whitespace-pre-wrap">{migrationDetail()!.sql}</pre>
                </div>
              </Show>
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  );
}

function DataTable(props: {
  columns: string[];
  rows: unknown[][];
  schema: ColumnInfo[];
  editingRow: number | null;
  rowEdits: Record<number, Record<number, string>>;
  onRowClick: (rowIdx: number) => void;
  onDelete: (rowIdx: number) => void;
  deletingRow: number | null;
  setDeletingRow: (idx: number | null) => void;
  table: string;
}) {
  const hasActions = () => props.table !== "";
  const isPK = (col: string) => col.toLowerCase() === "id";

  return (
    <table class="w-full text-xs font-mono">
      <thead class="bg-gray-900 text-gray-400 sticky top-0">
        <tr>
          <th class="text-left px-3 py-2 font-medium border-b border-gray-800 select-none w-8"></th>
          <For each={props.columns}>
            {(col) => (
              <th class={`text-left px-3 py-2 font-medium border-b border-gray-800 select-none ${isPK(col) ? "text-cyan-400" : ""}`}>{col}</th>
            )}
          </For>
          <Show when={hasActions()}><th class="text-right px-3 py-2 font-medium border-b border-gray-800 select-none w-16">Act</th></Show>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-800/50">
        <For each={props.rows}>
          {(row, i) => {
            const isEditing = () => props.editingRow === i();
            return (
              <tr
                class={`hover:bg-gray-900/30 cursor-pointer ${props.deletingRow === i() ? "bg-red-900/20" : ""} ${isEditing() ? "bg-blue-950/30 border-l-2 border-cyan-500" : ""}`}
                onClick={() => { if (hasActions()) props.onRowClick(i()); }}
              >
                <td class="px-3 py-1.5 text-gray-600 select-none text-right">{i() + 1}</td>
                <For each={row}>
                  {(cell, j) => (
                    <td class={`px-3 py-1.5 max-w-64 truncate ${isPK(props.columns[j()]) ? "text-cyan-400" : "text-gray-300"}`} title={String(cell ?? "NULL")}>
                      {cell === null ? <span class="text-gray-600 italic">NULL</span> : String(cell)}
                    </td>
                  )}
                </For>
                <Show when={hasActions()}>
                  <td class="px-3 py-1.5 text-right whitespace-nowrap">
                    <Show when={props.deletingRow === i()} fallback={
                      <button onClick={(e) => { e.stopPropagation(); props.setDeletingRow(i()); }} class="text-gray-600 hover:text-red-400 cursor-pointer" title="Delete row">✕</button>
                    }>
                      <span class="text-xs text-red-400">Del?</span>
                      <button onClick={(e) => { e.stopPropagation(); props.onDelete(i()); }} class="ml-1 text-red-400 hover:text-red-300 cursor-pointer font-bold">Y</button>
                      <button onClick={(e) => { e.stopPropagation(); props.setDeletingRow(null); }} class="ml-1 text-gray-400 hover:text-gray-200 cursor-pointer">N</button>
                    </Show>
                  </td>
                </Show>
              </tr>
            );
          }}
        </For>
      </tbody>
    </table>
  );
}

function EditRowForm(props: {
  columns: string[];
  row: unknown[];
  edits: Record<number, string>;
  schema: ColumnInfo[];
  table: string;
  onCellEdit: (colIdx: number, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const isPK = (col: string) => col.toLowerCase() === "id";

  function buildSQL() {
    const sets: string[] = [];
    for (let j = 0; j < props.columns.length; j++) {
      const orig = props.row[j] === null ? "" : String(props.row[j]);
      const edited = props.edits[j] ?? orig;
      if (edited === orig) continue;
      const val = edited === "" ? "NULL" : `'${edited.replace(/'/g, "''")}'`;
      sets.push(`"${props.columns[j]}" = ${val}`);
    }
    if (sets.length === 0) return "(no changes)";
    const idCol = props.schema.find((c) => c.name.toLowerCase() === "id")?.name;
    const idIdx = props.columns.indexOf(idCol ?? "id");
    const idVal = idIdx >= 0 ? props.row[idIdx] : null;
    if (!idVal) return "(no ID column found)";
    return `UPDATE "${props.table}" SET ${sets.join(", ")} WHERE "${idCol ?? "id"}" = '${String(idVal).replace(/'/g, "''")}';`;
  }

  return (
    <div class="border-t border-gray-700 bg-gray-950 p-4">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs text-gray-400">Edit Row — modify values and click Save</span>
        <div class="flex gap-2">
          <button onClick={props.onSave} class="px-3 py-1 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded text-xs hover:bg-emerald-900/50 cursor-pointer">Save</button>
          <button onClick={props.onCancel} class="px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:text-gray-200 cursor-pointer">Cancel</button>
        </div>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <For each={props.columns}>
          {(col, j) => (
            <div class="flex flex-col gap-1">
              <label class={`text-xs truncate ${isPK(col) ? "text-cyan-400" : "text-gray-500"}`} title={col}>{col}</label>
              <input
                value={props.edits[j()] ?? (props.row[j()] === null ? "" : String(props.row[j()]))}
                onInput={(e) => props.onCellEdit(j(), e.currentTarget.value)}
                readOnly={isPK(col)}
                class={`bg-gray-800 border rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-cyan-600 ${isPK(col) ? "border-gray-600 opacity-60 cursor-not-allowed" : "border-gray-700"}`}
              />
            </div>
          )}
        </For>
      </div>
      <div class="bg-gray-900 border border-gray-700 rounded px-3 py-2">
        <div class="text-xs text-gray-500 mb-1">SQL Preview:</div>
        <div class="text-xs font-mono text-gray-300 break-all">{buildSQL()}</div>
      </div>
    </div>
  );
}

function DataTableStatic(props: { columns: string[]; rows: unknown[][] }) {
  return (
    <table class="w-full text-xs font-mono">
      <thead class="bg-gray-900 text-gray-400 sticky top-0">
        <tr>
          <th class="text-left px-3 py-2 font-medium border-b border-gray-800 select-none">#</th>
          <For each={props.columns}>
            {(col) => <th class="text-left px-3 py-2 font-medium border-b border-gray-800 select-none">{col}</th>}
          </For>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-800/50">
        <For each={props.rows}>
          {(row, i) => (
            <tr class="hover:bg-gray-900/30">
              <td class="px-3 py-1.5 text-gray-600 select-none">{i() + 1}</td>
              <For each={row}>
                {(cell) => (
                  <td class="px-3 py-1.5 text-gray-300 max-w-64 truncate" title={String(cell ?? "NULL")}>
                    {cell === null ? <span class="text-gray-600 italic">NULL</span> : String(cell)}
                  </td>
                )}
              </For>
            </tr>
          )}
        </For>
      </tbody>
    </table>
  );
}

